import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowPipelineActionApiService } from '../services/workflow-pipeline-action-api.service';
import {
  WorkflowPipelineActionType,
  WorkflowPipelineActionResponse,
  PIPELINE_ACTION_TEST_EXAMPLES,
  TICKET_PIPELINE_ACTIONS
} from '../models/workflow-pipeline-action.model';
import { EntitySelectComponent } from '../../../../shared/components/entity-select/entity-select.component';
import { WorkflowRecordSelect } from '../shared/workflow-record-select.helper';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';
import { DealCycle } from '../../../deals/models/deal.model';
import { workflowRecordLabel, workflowStatusLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-pipeline-action-tester',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, EntitySelectComponent],
  templateUrl: './workflow-pipeline-action-tester.component.html'
})
export class WorkflowPipelineActionTesterComponent implements OnInit, OnDestroy {
  actionType: WorkflowPipelineActionType = 'CHANGE_DEAL_STAGE';
  objectType = 'DEAL';
  objectId: number | null = null;

  // Campos amigables (reemplazan el JSON crudo — inc 18)
  targetStageId: number | null = null;
  targetPipelineId: number | null = 1;
  closeDateMode: 'RELATIVE' | 'ABSOLUTE' = 'RELATIVE';
  closeDateAmount = 30;
  closeDateUnit: 'DAYS' | 'WEEKS' | 'MONTHS' = 'DAYS';
  closeDateAbsolute = '';
  preserveStage = false;
  reason = '';
  continueOnError = false;

  // Acciones sobre tickets: el ticket destino y el valor a aplicar viajan en la propia acción,
  // porque el registro inscrito es un negocio/contacto/empresa, nunca un ticket.
  ticketNumber = '';
  ticketStatusName = '';
  ticketPriorityName = '';
  ticketPipelineName = '';

  dealCycles: DealCycle[] = [];

  // Selector de registro por nombre
  recordSelect!: WorkflowRecordSelect;
  recordDisplayText = '';

  loading = false;
  error = '';
  result: WorkflowPipelineActionResponse | null = null;

  readonly actionTypeOptions: { value: WorkflowPipelineActionType; label: string; disabled: boolean }[] = [
    { value: 'CHANGE_DEAL_STAGE',      label: 'Cambiar etapa del negocio',            disabled: false },
    { value: 'UPDATE_DEAL_CLOSE_DATE', label: 'Actualizar fecha de cierre del negocio', disabled: false },
    { value: 'CHANGE_PIPELINE',        label: 'Cambiar pipeline / ciclo de negocio',   disabled: false },
    { value: 'CHANGE_TICKET_STATUS',   label: 'Cambiar estado de ticket',              disabled: false },
    { value: 'CHANGE_TICKET_PRIORITY', label: 'Cambiar prioridad de ticket',           disabled: false },
    { value: 'CHANGE_TICKET_PIPELINE', label: 'Cambiar pipeline de ticket',            disabled: false }
  ];

  // INC_CRM_020 (8) inc26-46 — `value` = clave exacta del backend, `label` = único texto traducible.
  readonly objectTypeOptions: { value: string; label: string }[] = [
    { value: 'DEAL', label: 'Negocio' },
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'COMPANY', label: 'Empresa' },
    { value: 'TICKET', label: 'Ticket' }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private pipelineApi: WorkflowPipelineActionApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService
  ) {}

  ngOnInit(): void {
    this.recordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.objectType,
      () => this.authService.getCurrentUser()?.businessId || 1
    );
    this.dealApi.getDealCycles().pipe(takeUntil(this.destroy$)).subscribe({
      next: (cycles) => { this.dealCycles = cycles; },
      error: () => {}
    });
  }

  onObjectTypeChange(): void {
    this.objectId = null;
    this.recordDisplayText = '';
  }

  onRecordSelected(item: any | null): void {
    this.recordDisplayText = item ? this.recordSelect.label(item) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isTicketAction(): boolean {
    return (TICKET_PIPELINE_ACTIONS as string[]).includes(this.actionType);
  }

  get hasExample(): boolean {
    return this.actionType in PIPELINE_ACTION_TEST_EXAMPLES;
  }

  get resultJson(): string {
    return JSON.stringify(this.result?.output ?? {}, null, 2);
  }

  get errorJson(): string {
    return JSON.stringify(this.result?.error ?? {}, null, 2);
  }

  get isChangeDealStage(): boolean { return this.actionType === 'CHANGE_DEAL_STAGE'; }
  get isUpdateCloseDate(): boolean { return this.actionType === 'UPDATE_DEAL_CLOSE_DATE'; }
  get isChangePipeline(): boolean  { return this.actionType === 'CHANGE_PIPELINE'; }

  onActionTypeChange(): void {
    // Una acción sobre tickets NO implica que el workflow sea de tickets: el registro inscrito
    // sigue siendo un negocio/contacto/empresa y el ticket se indica dentro de la acción. Forzar
    // aquí objectType='TICKET' hacía que el backend rechazara la prueba por tipo de objeto.
    if (this.objectType === 'TICKET') { this.objectType = 'DEAL'; }
    this.result = null;
    this.error = '';
  }

  /** Construye el config JsonNode internamente desde los campos amigables. */
  private buildConfig(): Record<string, any> {
    const base: Record<string, any> = {
      dealIdSource: 'CURRENT_RECORD',
      reason: this.reason || undefined,
      continueOnError: this.continueOnError
    };
    if (this.actionType === 'CHANGE_DEAL_STAGE') {
      return { ...base, targetStageId: this.targetStageId };
    }
    if (this.actionType === 'UPDATE_DEAL_CLOSE_DATE') {
      if (this.closeDateMode === 'ABSOLUTE') {
        return { ...base, mode: 'ABSOLUTE', targetDate: this.closeDateAbsolute, timezone: 'America/Lima' };
      }
      return { ...base, mode: 'RELATIVE', amount: this.closeDateAmount, unit: this.closeDateUnit, timezone: 'America/Lima' };
    }
    if (this.actionType === 'CHANGE_PIPELINE') {
      return { ...base, targetPipelineId: this.targetPipelineId, targetStageId: this.targetStageId, preserveStageIfPossible: this.preserveStage };
    }
    if (this.isTicketAction) {
      const ticketBase: Record<string, any> = {
        ticketNumber: this.ticketNumber || undefined,
        continueOnError: this.continueOnError
      };
      if (this.actionType === 'CHANGE_TICKET_STATUS') {
        return { ...ticketBase, statusName: this.ticketStatusName || undefined };
      }
      if (this.actionType === 'CHANGE_TICKET_PRIORITY') {
        return { ...ticketBase, priorityName: this.ticketPriorityName || undefined };
      }
      return { ...ticketBase, pipelineName: this.ticketPipelineName || undefined };
    }
    return base;
  }

  test(): void {
    this.loading = true;
    this.error = '';
    this.result = null;

    this.pipelineApi.test({
      actionType: this.actionType,
      objectType: this.objectType,
      ...(this.objectId ? { objectId: this.objectId } : {}),
      config: this.buildConfig(),
      context: {}
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.result = res; this.loading = false; },
      error: (err) => {
        const msg = err.error?.message ?? '';
        if (err.status === 403) {
          this.error = 'No tienes permisos para realizar esta acción.';
        } else if (msg.toLowerCase().includes('ticket')) {
          this.error = 'No se pudo procesar la solicitud sobre el ticket.';
        } else if (msg.toLowerCase().includes('negocio') || msg.toLowerCase().includes('deal')) {
          this.error = msg || 'No se pudo resolver el negocio para la acción de pipeline.';
        } else if (msg.toLowerCase().includes('etapa') || msg.toLowerCase().includes('stage')) {
          this.error = msg || 'La etapa destino no existe o no pertenece al pipeline indicado.';
        } else if (msg.toLowerCase().includes('pipeline')) {
          this.error = msg || 'El pipeline destino no existe o no pertenece al business.';
        } else {
          this.error = msg || 'Error al ejecutar la acción de pipeline.';
        }
        this.loading = false;
      }
    });
  }

  /**
   * INC_CRM_020 (8) inc26-46 — el badge pintaba el enum crudo del backend (SUCCESS/FAILED/SKIPPED)
   * y la fila de detalle el `actionType` técnico. El valor que llega NO se toca; solo se traduce
   * lo que se muestra, y si aparece un estado/tipo desconocido se enseña tal cual.
   */
  /** Nombre del registro afectado: antes se pintaba «CONTACT #42». */
  readonly recordText = workflowRecordLabel;

  /**
   * Estado en español. El vocabulario vive en el diccionario único del módulo
   * (`models/workflow-labels.ts`); antes cada probador repetía su propio `switch` de tres casos y
   * cualquier otro estado del motor (`RETRY_SCHEDULED`, `PENDING`…) se colaba en crudo.
   */
  /**
   * Nombre de la etapa del negocio a partir de su id. La salida de la acción solo trae
   * `oldStageId` / `newStageId` (enteros de `deal_cycle`), pero el probador ya tiene el catálogo
   * cargado para su propio desplegable, así que no hace falta pedirlo otra vez.
   */
  stageName(stageId: unknown): string {
    if (stageId === null || stageId === undefined || stageId === '') {
      return '—';
    }
    const id = Number(stageId);
    return this.dealCycles.find((c) => c.id === id)?.name ?? `Etapa #${stageId}`;
  }

  statusLabel(status: string | undefined): string {
    return workflowStatusLabel(status, 'Desconocido');
  }

  actionTypeLabel(type: string | undefined): string {
    if (!type) {
      return '—';
    }
    return this.actionTypeOptions.find((o) => o.value === type)?.label ?? type;
  }

  getStatusClass(status: string | undefined): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'FAILED':  return 'bg-red-100 text-red-700';
      case 'SKIPPED': return 'bg-gray-100 text-gray-600';
      default:        return 'bg-blue-100 text-blue-700';
    }
  }
}
